/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Swipe gestures (tyk-swipe.js)

   Adds touch-friendly swipe-to-dismiss to:
     • Cart drawer       — swipe down (or right on RTL) to close
     • Quick-view modal  — swipe down to close
     • Quiz modal sheet  — swipe down to close

   Uses Pointer Events so it works on touch and pen, no library needed.
   Mobile nav panel + product gallery already have native swipe (touch
   handler on the handle, CSS scroll-snap respectively).

   Behavior:
     • Threshold: 80px vertical drag past start
     • Velocity: >0.4 px/ms also closes (flick gesture)
     • Real-time follow during drag for tactile feedback
     • Releases below threshold → snap back via CSS transition
     • Disabled on prefers-reduced-motion (still allows close, but no
       finger-following animation)

   Closing strategy:
     • cart-drawer            → call its .close() method
     • [data-modal] / .modal  → click the close button
     • .tyk-quiz              → dispatch click on [data-tyk-quiz-close]
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const THRESHOLD_PX = 80;
  const VELOCITY_PX_PER_MS = 0.4;

  const reducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)');

  function isCoarse() {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  }

  function attachSwipe(target, opts) {
    if (!target || target.__tykSwipeBound) return;
    target.__tykSwipeBound = true;

    let startY = 0;
    let startX = 0;
    let startT = 0;
    let dragging = false;
    let lastY = 0;

    function reset(animateBack) {
      const sheet = opts.sheet || target;
      sheet.style.transition = animateBack && !(reducedMotion && reducedMotion.matches)
        ? 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)'
        : '';
      sheet.style.transform = '';
      // Clear after the snap-back so future drags aren't constrained.
      setTimeout(() => { sheet.style.transition = ''; }, 240);
      dragging = false;
    }

    function onPointerDown(e) {
      if (!isCoarse()) return; // skip on mouse — they have a close button
      // Don't start drags from inside scrollable content unless at top
      const scrollEl = opts.sheet && opts.sheet.scrollTop != null ? opts.sheet : target;
      if (scrollEl.scrollTop > 0) return;
      // Don't hijack from form inputs / interactive controls
      if (e.target.closest('input, textarea, select, button, [role="button"], a')) {
        // Let interactive controls win — but if the drag continues we'll catch
        // it at pointermove anyway. So just don't capture here.
        return;
      }
      startY = e.clientY;
      startX = e.clientX;
      startT = Date.now();
      lastY = startY;
      dragging = true;
      try { target.setPointerCapture(e.pointerId); } catch (_) {}
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const dy = e.clientY - startY;
      const dx = e.clientX - startX;
      // Lock to vertical: if horizontal exceeds vertical, abandon.
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 16) {
        dragging = false;
        reset(true);
        return;
      }
      if (dy <= 0) {
        // Pulling up — let users drag a few px for elastic effect
        const sheet = opts.sheet || target;
        sheet.style.transform = `translateY(${dy * 0.2}px)`;
        return;
      }
      lastY = e.clientY;
      const sheet = opts.sheet || target;
      sheet.style.transform = `translateY(${dy}px)`;
    }

    function onPointerUp(e) {
      if (!dragging) return;
      const dy = e.clientY - startY;
      const dt = Math.max(1, Date.now() - startT);
      const velocity = (e.clientY - startY) / dt;
      const flicked = velocity > VELOCITY_PX_PER_MS && dy > 30;
      const passedThreshold = dy > THRESHOLD_PX;

      if (passedThreshold || flicked) {
        // Snap to closed: animate the rest of the way down, then close
        const sheet = opts.sheet || target;
        if (!(reducedMotion && reducedMotion.matches)) {
          sheet.style.transition = 'transform 180ms ease-out';
          sheet.style.transform = `translateY(100%)`;
        }
        if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('tap');
        setTimeout(() => {
          opts.close && opts.close();
          // Reset transform after close so reopening starts clean
          setTimeout(() => reset(false), 50);
        }, 160);
      } else {
        reset(true);
      }
    }

    function onPointerCancel() {
      reset(true);
    }

    target.addEventListener('pointerdown', onPointerDown, { passive: true });
    target.addEventListener('pointermove', onPointerMove, { passive: true });
    target.addEventListener('pointerup', onPointerUp, { passive: true });
    target.addEventListener('pointercancel', onPointerCancel, { passive: true });
  }

  // ── Targets ─────────────────────────────────────────────────────────

  function wireCartDrawer() {
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      const sheet = drawer.querySelector('.drawer__inner') || drawer;
      attachSwipe(drawer, {
        sheet: drawer, // entire drawer transforms — feels native
        close: function () {
          if (typeof drawer.close === 'function') drawer.close();
        },
      });
    });
  }

  function wireQuiz() {
    // Quiz is built dynamically; attach via mutation observer
    const mo = new MutationObserver(function () {
      document.querySelectorAll('.tyk-quiz').forEach(function (quiz) {
        const sheet = quiz.querySelector('.tyk-quiz__sheet');
        if (!sheet) return;
        attachSwipe(sheet, {
          sheet: sheet,
          close: function () {
            const closer = quiz.querySelector('[data-tyk-quiz-close]');
            if (closer) closer.click();
          },
        });
      });
    });
    if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  }

  function wireQuickView() {
    const qv = document.getElementById('tyk-quick-view');
    if (!qv) return;
    const sheet = qv.querySelector('.tyk-qv__sheet') || qv.firstElementChild;
    if (!sheet) return;
    attachSwipe(sheet, {
      sheet: sheet,
      close: function () {
        const closer = qv.querySelector('[data-tyk-qv-close], .tyk-qv__close');
        if (closer) closer.click();
        else qv.setAttribute('aria-hidden', 'true');
      },
    });
  }

  function init() {
    wireCartDrawer();
    wireQuiz();
    wireQuickView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
